// What the GIF Audit screen decides, kept apart from the screen so it can be tested.
//
// A demo address is only "valid" when the file was asked for and came back as a picture. The
// screen used to trust any address of the form /exercises/<name>.gif whose name was on its
// built-in list; the app has no such files and answers with its own front page, so eleven
// exercises with a dead demo were shown as valid (found 8 October 2026).

const STORAGE_PATH = '/storage/v1/object/public/exercises/';

// Where a demo file lives in the public "exercises" bucket.
export const storageDemoAddress = (file, supabaseUrl = process.env.REACT_APP_SUPABASE_URL) =>
  `${String(supabaseUrl || '').replace(/\/+$/, '')}${STORAGE_PATH}${file}`;

// The small still made beside each video; used as the picker's thumbnail instead of the whole GIF.
export const storageDemoStill = (file, supabaseUrl) =>
  storageDemoAddress(`video/${file.replace(/\.gif$/, '.jpg')}`, supabaseUrl);

// Ask for the address and report 'ok' | 'missing' | 'unknown'.
// 'ok' needs a picture: a page answered in place of a missing file is 'missing'.
export async function checkDemoAddress(url, { fetchImpl = fetch, deadline = Infinity, wait = (ms) => new Promise(r => setTimeout(r, ms)), now = Date.now } = {}, attempt = 0) {
  if (now() > deadline) return 'unknown';
  try {
    const res = await fetchImpl(url, { method: 'HEAD' });
    if (res.ok) {
      const type = (res.headers && res.headers.get && res.headers.get('content-type')) || '';
      return /^image\//i.test(type) ? 'ok' : 'missing';
    }
    if (res.status === 429 && attempt < 2 && now() < deadline) {
      await wait(600 * (attempt + 1));
      return checkDemoAddress(url, { fetchImpl, deadline, wait, now }, attempt + 1);
    }
    if ([400, 404, 410].includes(res.status)) return 'missing';
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

// Build the audit view model from the exercise rows plus a map of
// address -> 'ok' | 'missing' | 'unknown'. Addresses absent from the map render
// as 'checking' (verification still running / not started). Pure so it can be
// called once for the immediate render and again once verification finishes.
export const analyzeGifAudit = (data, stateByUrl) => {
  const analyzed = (data || []).map(ex => {
    const hasGif = Boolean(ex.gif_url && ex.gif_url.trim() !== '');
    const gifState = hasGif ? (stateByUrl[ex.gif_url] || 'checking') : 'missing'; // 'ok' | 'missing' | 'unknown' | 'checking'

    const gifExists = gifState === 'ok';
    const status = !hasGif
      ? 'missing'
      : gifState === 'ok'
        ? 'valid'
        : gifState === 'missing'
          ? 'broken'
          : gifState === 'checking'
            ? 'checking'
            : 'unverified';

    return { ...ex, hasGif, gifExists, status };
  });

  const gifUsage = {};
  analyzed.forEach(ex => {
    if (ex.hasGif && ex.gifExists) {
      const filename = ex.gif_url.split('/').pop();
      if (!gifUsage[filename]) gifUsage[filename] = [];
      gifUsage[filename].push(ex.name);
    }
  });
  const duplicateFilenames = new Set(
    Object.entries(gifUsage).filter(([, names]) => names.length > 1).map(([f]) => f)
  );

  const analyzedWithDupes = analyzed.map(ex => {
    if (!ex.hasGif || !ex.gifExists) return { ...ex, isDuplicate: false, duplicateWith: [] };
    const filename = ex.gif_url.split('/').pop();
    const isDuplicate = duplicateFilenames.has(filename);
    return {
      ...ex,
      isDuplicate,
      duplicateWith: isDuplicate ? gifUsage[filename].filter(n => n !== ex.name) : [],
    };
  });

  const withGif = analyzedWithDupes.filter(e => e.hasGif).length;
  const stats = {
    total: analyzedWithDupes.length,
    withGif,
    withoutGif: analyzedWithDupes.length - withGif,
    validGif: analyzedWithDupes.filter(e => e.status === 'valid').length,
    brokenGif: analyzedWithDupes.filter(e => e.status === 'broken').length,
    unverifiedGif: analyzedWithDupes.filter(e => e.status === 'unverified').length,
    checkingGif: analyzedWithDupes.filter(e => e.status === 'checking').length,
    duplicateGif: analyzedWithDupes.filter(e => e.isDuplicate).length,
  };

  return { analyzedWithDupes, stats };
};
